import { Alert, Button, Group, Select, Stack, Text, Tooltip } from '@mantine/core';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import {
  IconArrowBackUp,
  IconArrowLeft,
  IconCards,
  IconCheck,
  IconClock,
  IconPlayerPlay,
} from '@tabler/icons-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import type {
  DrawSpreadCardInput,
  MoveSpreadCardInput,
  SpreadParticipantView,
  SpreadPlacedCardView,
  SpreadSessionView,
} from '@creativitas/shared';
import { SessionStatusBadge } from '@/components/domain/SessionStatusBadge';
import { InviteButton } from '@/components/domain/InviteButton';
import { PageHeader } from '@/components/ui/PageHeader';
import { ErrorState, LoadingState } from '@/components/ui/StateViews';
import { toUserMessage } from '@/lib/api';
import { pluralize } from '@/lib/plural';
import {
  clearSpreadTable,
  completeSpreadSession,
  drawSpreadCard,
  fetchSpreadSession,
  moveSpreadCard,
  removeSpreadCard,
  removeSpreadParticipant,
  setSpreadTurn,
  startSpreadSession,
  SPREAD_SESSIONS_KEY,
  setSpreadEntry,
  spreadSessionKey,
  undoSpreadCard,
} from './api';
import { applySession, keepInvite, keepNewestSession, newerSession } from './session-cache';
import { participantName } from './spread-table';
import { FreeformSpread } from './FreeformSpread';
import { SpreadCardsPanel, SpreadProgressByParticipant } from './SpreadCardsPanel';
import { PresetSpread } from './PresetSpread';
import { SessionParticipants } from './SessionParticipants';
import { SpreadTurnControl } from './SpreadTurnControl';
import { useSessionRealtime, type RealtimeStatus } from './useSessionRealtime';

export function SpreadSessionPage() {
  const { sessionId = '' } = useParams();
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: spreadSessionKey(sessionId),
    queryFn: () => fetchSpreadSession(sessionId),
    enabled: Boolean(sessionId),
    /*
      Вернулись на вкладку — сверяемся с сервером. Фоновую вкладку браузер
      усыпляет, и снимки, пришедшие за это время, могли не дойти.
    */
    refetchOnWindowFocus: 'always',
    /* Перезапрос не откатывает стол к состоянию старше уже показанного. */
    structuralSharing: keepNewestSession,
  });

  /* Чей расклад на столе: выбирает ведущий; `null` — пока никого не выбрали. */
  const [viewedSeat, setViewedSeat] = useState<string | null>(null);
  const draws = useNewCardsNotice((participantId) => setViewedSeat(participantId));

  const realtimeStatus = useSessionRealtime<SpreadSessionView>({
    sessionId,
    enabled: Boolean(sessionId) && query.data?.isSolo === false,
    onSnapshot: (snapshot) => {
      const previous = queryClient.getQueryData<SpreadSessionView>(spreadSessionKey(sessionId));
      /* Опоздавший снимок старше показанного: ни стол, ни сообщения он не трогает. */
      if (previous && newerSession(previous, snapshot) === previous) return;
      if (previous) draws.announce(previous, snapshot);
      applySession(queryClient, spreadSessionKey(sessionId), snapshot, keepInvite);
    },
  });

  const apply = (session: SpreadSessionView) =>
    applySession(queryClient, spreadSessionKey(sessionId), session, keepInvite);
  const commit = (session: SpreadSessionView) => {
    apply(session);
    void queryClient.invalidateQueries({ queryKey: SPREAD_SESSIONS_KEY });
  };
  const fail = (error: unknown) => notifications.show({ color: 'red', message: toUserMessage(error) });

  const draw = useMutation({
    mutationFn: (input: DrawSpreadCardInput) => drawSpreadCard(sessionId, input),
    onSuccess: commit,
    onError: fail,
  });
  const move = useMutation({
    mutationFn: ({ cardId, input }: { cardId: string; input: MoveSpreadCardInput }) =>
      moveSpreadCard(sessionId, cardId, input),
    onSuccess: commit,
    onError: fail,
  });
  const remove = useMutation({
    mutationFn: (cardId: string) => removeSpreadCard(sessionId, cardId),
    onSuccess: commit,
    onError: fail,
  });

  const clear = useMutation({
    mutationFn: () => clearSpreadTable(sessionId),
    onSuccess: commit,
    onError: fail,
  });
  const complete = useMutation({
    mutationFn: () => completeSpreadSession(sessionId),
    onSuccess: (session) => {
      commit(session);
      notifications.show({ color: 'teal', message: 'Сессия завершена и сохранена' });
    },
    onError: fail,
  });
  const entry = useMutation({
    mutationFn: (open: boolean) => setSpreadEntry(sessionId, open),
    onSuccess: apply,
    onError: (error) => notifications.show({ color: 'red', message: toUserMessage(error) }),
  });
  const start = useMutation({
    mutationFn: () => startSpreadSession(sessionId),
    onSuccess: commit,
    onError: fail,
  });

  /* Очередь участников: ведущий назначает, кто тянет (§бэклог 9). */
  const turn = useMutation({
    mutationFn: (participantId: string | null) => setSpreadTurn(sessionId, participantId),
    onSuccess: apply,
    onError: (error) => notifications.show({ color: 'red', message: toUserMessage(error) }),
  });
  const undo = useMutation({
    mutationFn: (participantId: string) => undoSpreadCard(sessionId, participantId),
    onSuccess: (session) => {
      commit(session);
      notifications.show({ color: 'teal', message: 'Карта отменена и вернулась в колоду' });
    },
    onError: fail,
  });
  const removeParticipant = useMutation({
    mutationFn: (participantId: string) => removeSpreadParticipant(sessionId, participantId),
    onSuccess: commit,
    onError: fail,
  });

  const cardCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const card of query.data?.placedCards ?? []) {
      if (card.placedByParticipantId) {
        counts[card.placedByParticipantId] = (counts[card.placedByParticipantId] ?? 0) + 1;
      }
    }
    return counts;
  }, [query.data?.placedCards]);

  if (query.isLoading) return <LoadingState label="Восстанавливаем сессию..." />;
  if (query.isError || !query.data) return <ErrorState onRetry={() => void query.refetch()} />;

  const session = query.data;
  /*
    Чей расклад на столе. Выбор ведущего живёт здесь, а не в столе: стол — про
    то, как показать, а не про то, кого показывать.

    Пока никого не выбрали, показывается первый пришедший участник, а в
    индивидуальной сессии — единственный, кто за столом. Ведущий сам в списке
    тоже есть: в групповой сессии он может раскладывать наравне.
  */
  const seats = session.participants;
  const viewed =
    viewedSeat && seats.some((seat) => seat.id === viewedSeat)
      ? viewedSeat
      : (seats.find((seat) => seat.kind !== 'facilitator') ?? seats[0])?.id;

  /* Перемещение карты не в счёт: стол показывает его сразу и сохраняет сам. */
  const busy = draw.isPending || remove.isPending || clear.isPending || complete.isPending ||
    start.isPending || turn.isPending || undo.isPending || removeParticipant.isPending;

  /* Последняя карта того, чей расклад на столе, — её и предлагает отменить кнопка. */
  const lastOfViewed = latestCardOf(session.placedCards, viewed);
  const viewedName = viewed ? participantName(session, viewed) : undefined;
  const hostSeatId = seats.find((seat) => seat.kind === 'facilitator')?.id;

  const askUndo = () => {
    if (!viewed || !lastOfViewed) return;
    const label =
      session.spread.positions.find((position) => position.id === lastOfViewed.positionId)?.title ??
      lastOfViewed.deckTitle;
    modals.openConfirmModal({
      title: 'Отменить последнюю карту?',
      children: (
        <Text size="sm" c="var(--text-muted)">
          Карта «{label}» {viewed === hostSeatId ? 'из вашего расклада' : `участника ${viewedName ?? ''}`}{' '}
          вернётся в колоду, позиция освободится, и карту можно будет вытянуть заново.
        </Text>
      ),
      labels: { confirm: 'Отменить карту', cancel: 'Оставить' },
      onConfirm: () => undo.mutate(viewed),
    });
  };

  const askRemove = (participant: SpreadParticipantView) => {
    modals.openConfirmModal({
      title: `Удалить участника «${participant.displayName}»?`,
      children: (
        <Text size="sm" c="var(--text-muted)">
          Он потеряет доступ к сессии. Его карты останутся в истории. Войти снова по ссылке он
          сможет только как новый участник и только пока вход открыт.
        </Text>
      ),
      labels: { confirm: 'Удалить', cancel: 'Отмена' },
      confirmProps: { color: 'red' },
      onConfirm: () => removeParticipant.mutate(participant.id),
    });
  };

  /** Preset spreads need every position drawn; the free-form table has none. */
  const positionsToFill = session.spread.positions.length;
  const canComplete =
    session.spread.layout !== 'preset' || session.placedCards.length >= positionsToFill;

  const askComplete = () => {
    modals.openConfirmModal({
      title: 'Завершить сессию?',
      children: (
        <Text size="sm" c="var(--text-muted)">
          Изменять расклад после завершения будет нельзя. Посмотреть карты можно ещё час, затем сессия
          удаляется: архив сессий не ведётся.
        </Text>
      ),
      labels: { confirm: 'Завершить', cancel: 'Продолжить работу' },
      confirmProps: { color: 'teal' },
      onConfirm: () => complete.mutate(),
    });
  };

  return (
    <>
      <PageHeader
        eyebrow={session.isSolo ? 'Индивидуальная сессия' : 'Групповая сессия'}
        title={session.spread.title}
        description={
          <Group gap="xs">
            <SessionStatusBadge status={session.status} />
            <Text component="span" size="sm">
              {pluralize(session.placedCards.length, 'карта', 'карты', 'карт')} · состояние
              сохранено на сервере
            </Text>
          </Group>
        }
        actions={
          <Group gap="sm">
            <Button
              component={Link}
              to={`/spreads/${session.spread.slug}`}
              variant="subtle"
              leftSection={<IconArrowLeft size={16} />}
            >
              К описанию
            </Button>
            {/*
              Приглашение — здесь, рядом с остальными действиями ведущего.
              Панелью над столом оно занимало место всю сессию, а нужно один
              раз, в самом начале.
            */}
            {!session.isSolo && session.status !== 'completed' && session.inviteToken ? (
              <InviteButton
                inviteUrl={`${window.location.origin}/join/${session.inviteToken}`}
                entryOpen={session.entryOpen}
                busy={entry.isPending}
                onToggle={(open) => entry.mutate(open)}
              />
            ) : null}
            {session.status === 'active' ? (
              /*
                A preset spread can only be completed once every position is
                filled — the server enforces that. Offering a button that will
                be refused is worse than explaining why it is not ready yet.
              */
              <Tooltip
                label={`Заполните все позиции: ${session.placedCards.length} из ${positionsToFill}`}
                disabled={canComplete}
              >
                <span>
                  <Button
                    variant="light"
                    loading={complete.isPending}
                    disabled={!canComplete}
                    onClick={askComplete}
                  >
                    Завершить
                  </Button>
                </span>
              </Tooltip>
            ) : null}
          </Group>
        }
      />

      {session.status === 'waiting' && !session.isSolo ? (
        <GroupSessionShell
          session={session}
          realtimeStatus={realtimeStatus}
          starting={start.isPending}
          onStart={() => start.mutate()}
          busy={busy}
          onRemove={askRemove}
        />
      ) : (
        <Stack gap="md">
          {!session.isSolo ? (
            <>
              <RealtimeNotice status={realtimeStatus} />
              <SessionParticipants
                participants={session.participants}
                cardCounts={cardCounts}
                recentIds={draws.recentIds}
                busy={busy}
                onRemove={session.status === 'completed' ? undefined : askRemove}
              />
              {session.status === 'active' ? (
                <SpreadTurnControl
                  session={session}
                  busy={busy}
                  onSet={(participantId) => turn.mutate(participantId)}
                />
              ) : null}
            </>
          ) : null}
          {session.status === 'completed' ? (
            <Alert color="teal" icon={<IconCheck size={18} />} title="Сессия завершена">
              Сохранённые карты можно рассмотреть. Для новой работы запустите новый расклад из каталога.
            </Alert>
          ) : null}

          {session.spread.layout === 'preset' ? (
            <>
              {/*
                У каждого участника свой расклад, поэтому стол показывает
                раскладку одного человека. Кого — выбирает ведущий; в
                индивидуальной сессии выбирать не из кого, и переключателя нет.
              */}
              {viewed && (session.participants.length > 1 || session.status === 'active') ? (
                <Stack gap={4}>
                  <Group gap="sm" align="center" justify="space-between" wrap="wrap">
                    {session.participants.length > 1 ? (
                      <Group gap="sm" align="center">
                        <Text size="sm" c="var(--text-muted)">Чей расклад на столе</Text>
                        <Select
                          size="xs"
                          w={220}
                          data={session.participants.map((participant) => ({
                            value: participant.id,
                            label: participant.displayName,
                          }))}
                          value={viewed}
                          onChange={(next: string | null) => next && setViewedSeat(next)}
                          allowDeselect={false}
                          comboboxProps={{ withinPortal: true }}
                          aria-label="Чей расклад показывать"
                        />
                      </Group>
                    ) : (
                      <span />
                    )}
                    {session.status === 'active' ? (
                      <Button
                        size="xs"
                        variant="subtle"
                        leftSection={<IconArrowBackUp size={15} />}
                        disabled={busy || !lastOfViewed}
                        loading={undo.isPending}
                        onClick={askUndo}
                        data-testid="spread-undo-card"
                      >
                        Отменить последнюю карту
                      </Button>
                    ) : null}
                  </Group>
                  {/*
                    Ведущий тянет в расклад того, кто на столе. Без этой строки
                    переключатель читался как «посмотреть», и было непонятно, куда
                    ляжет карта.
                  */}
                  {session.status === 'active' && viewed !== hostSeatId && viewedName ? (
                    <Text size="xs" c="var(--text-subtle)" data-testid="spread-draw-target">
                      Карта, которую вы вытянете, ляжет в расклад участника {viewedName}.
                    </Text>
                  ) : null}
                </Stack>
              ) : null}
              <PresetSpread
                session={session}
                busy={busy}
                viewedParticipantId={viewed}
                onDraw={(input) => draw.mutate({ ...input, forParticipantId: viewed })}
              />
              <SpreadCardsPanel session={session} facilitator />
              {/* SPR-15 — ведущий видит, кто из участников где находится. */}
              <SpreadProgressByParticipant session={session} />
            </>
          ) : (
            <>
              <FreeformSpread
                key={session.id}
                session={session}
                busy={busy}
                onDraw={(input) => draw.mutate(input)}
                onMove={(cardId, input) => move.mutateAsync({ cardId, input })}
                onClear={() => clear.mutate()}
                onRemove={(cardId) => remove.mutate(cardId)}
              />
              <SpreadCardsPanel session={session} facilitator />
            </>
          )}
        </Stack>
      )}
    </>
  );
}

function GroupSessionShell({
  session,
  realtimeStatus,
  starting,
  onStart,
  busy,
  onRemove,
}: {
  session: SpreadSessionView;
  realtimeStatus: RealtimeStatus;
  starting: boolean;
  onStart: () => void;
  busy: boolean;
  onRemove: (participant: SpreadParticipantView) => void;
}) {
  const guestCount = session.participants.filter((participant) => participant.kind === 'guest').length;

  return (
    <Stack gap="lg">
      <RealtimeNotice status={realtimeStatus} />

      {/*
        Панели приглашения здесь больше нет: ссылка живёт в шапке, одной
        кнопкой, и одинаково доступна до старта и во время сессии. Дублировать
        её отдельным блоком на экране ожидания значит показывать одно и то же
        дважды и отодвигать список участников вниз.
      */}
      <SessionParticipants participants={session.participants} busy={busy} onRemove={onRemove} />

      <Group justify="flex-end">
        <Button
          leftSection={<IconPlayerPlay size={17} />}
          disabled={guestCount === 0 || realtimeStatus !== 'connected'}
          loading={starting}
          onClick={onStart}
        >
          Начать сессию
        </Button>
      </Group>
    </Stack>
  );
}

/**
 * The room, while the spread is running (SPR-14).
 *
 * The invitation stays live after the start, so the facilitator needs the link
 * in front of them during the session — and a way to shut the door when the
 * circle is complete. Дарья named no moment for that to happen by itself, so
 * nothing does it automatically.
 */
/** Самая свежая карта участника — та, которую предлагает отменить кнопка. */
function latestCardOf(
  cards: SpreadPlacedCardView[],
  participantId: string | undefined,
): SpreadPlacedCardView | undefined {
  if (!participantId) return undefined;
  return cards
    .filter((card) => card.placedByParticipantId === participantId)
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt) || b.zIndex - a.zIndex)[0];
}

const NEW_CARDS_NOTICE = 'spread-new-cards';
/** Карты, пришедшие в пределах этого окна, собираются в одно сообщение. */
const NOTICE_WINDOW_MS = 5_000;
/** Сколько держится отметка «только что» в списке участников. */
const RECENT_MS = 10_000;

/**
 * Ведущий видит, кто вытянул карту (тест Дарьи, 16 сентября).
 *
 * Стол ведущего показывает расклад одного человека, и карта другого участника
 * при свободном порядке появлялась незаметно. Сообщение говорит, кто и что
 * вытянул; стол при этом сам не переключается — ведущий мог как раз
 * разбирать чужой расклад. «Показать» переключает по нажатию.
 *
 * Когда тянут сразу несколько человек, сообщения не сыплются стопкой: всё, что
 * пришло за пять секунд, собирается в одно.
 */
function useNewCardsNotice(onShow: (participantId: string) => void) {
  const [recent, setRecent] = useState<Record<string, number>>({});
  const batch = useRef<{ startedAt: number; byParticipant: Map<string, { name: string; count: number; label: string }> }>({
    startedAt: 0,
    byParticipant: new Map(),
  });
  const showRef = useRef(onShow);
  showRef.current = onShow;

  useEffect(() => {
    const expiries = Object.values(recent);
    if (expiries.length === 0) return undefined;
    const wait = Math.max(0, Math.min(...expiries) - Date.now()) + 50;
    const timer = window.setTimeout(() => {
      const now = Date.now();
      setRecent((current) =>
        Object.fromEntries(Object.entries(current).filter(([, until]) => until > now)),
      );
    }, wait);
    return () => window.clearTimeout(timer);
  }, [recent]);

  const recentIds = useMemo(() => new Set(Object.keys(recent)), [recent]);

  const announce = (previous: SpreadSessionView, next: SpreadSessionView) => {
    const known = new Set(previous.placedCards.map((card) => card.id));
    const hostSeat = next.participants.find((participant) => participant.kind === 'facilitator')?.id;
    /* Свои карты ведущий и так видит — сообщает только чужая рука. */
    const fresh = next.placedCards.filter(
      (card) =>
        !known.has(card.id) &&
        card.placedByParticipantId !== null &&
        card.placedByParticipantId !== hostSeat &&
        !card.drawnByFacilitator,
    );
    if (fresh.length === 0) return;

    const now = Date.now();
    if (now - batch.current.startedAt > NOTICE_WINDOW_MS) {
      batch.current = { startedAt: now, byParticipant: new Map() };
    }
    for (const card of fresh) {
      const participantId = card.placedByParticipantId!;
      const entry = batch.current.byParticipant.get(participantId);
      batch.current.byParticipant.set(participantId, {
        name: participantName(next, participantId) ?? 'Участник',
        count: (entry?.count ?? 0) + 1,
        label:
          next.spread.positions.find((position) => position.id === card.positionId)?.title ??
          card.deckTitle,
      });
    }
    setRecent((current) => {
      const updated = { ...current };
      for (const card of fresh) updated[card.placedByParticipantId!] = now + RECENT_MS;
      return updated;
    });

    const entries = [...batch.current.byParticipant.entries()];
    const total = entries.reduce((sum, [, entry]) => sum + entry.count, 0);
    const single = entries.length === 1 ? entries[0] : undefined;
    const text = single
      ? single[1].count === 1
        ? `${single[1].name} · ${single[1].label}`
        : `${single[1].name} · ${pluralize(single[1].count, 'карта', 'карты', 'карт')}`
      : entries.map(([, entry]) => entry.name).join(', ');

    const notice = {
      id: NEW_CARDS_NOTICE,
      color: 'teal',
      icon: <IconCards size={16} />,
      autoClose: 6_000,
      title: total === 1 ? 'Новая карта' : `Новые карты: ${total}`,
      message: (
        <Group justify="space-between" gap="sm" wrap="nowrap" data-testid="spread-new-cards-notice">
          <Text size="sm">{text}</Text>
          {single && next.spread.layout === 'preset' ? (
            <Button
              size="compact-xs"
              variant="light"
              onClick={() => {
                showRef.current(single[0]);
                notifications.hide(NEW_CARDS_NOTICE);
              }}
            >
              Показать
            </Button>
          ) : null}
        </Group>
      ),
    };
    /* Уже на экране — обновляем; закрыто или ещё не было — показываем. */
    notifications.update(notice);
    notifications.show(notice);
  };

  return { announce, recentIds };
}

function RealtimeNotice({ status }: { status: RealtimeStatus }) {
  if (status === 'connected') return null;

  return (
    <Alert color={status === 'error' ? 'red' : 'gold'} icon={<IconClock size={18} />} title="Переподключаемся">
      Свежая копия состояния будет загружена с сервера после восстановления связи.
    </Alert>
  );
}
